import dayjs from "dayjs";

import {
  evaluateConditional,
  getWeatherConditionValue,
  isWeatherConditionDepends,
  normalizeWeatherCompareValue,
  WEATHER_DEPENDS_LABELS,
  WEATHER_DEPENDS_OPTIONS
} from "../checksheet-builder/helpers.js";
import {exists} from "../../utils/helpers.js";

/** Weather pseudo-fields for Fields SearchSelect (display from past-record weather). */
export const REVIEW_WEATHER_FIELDS = [
  {name: "Rainfall", value: "rainfall"},
  {name: "Cumulative Rainfall", value: "cumulative"},
  {name: "Precipitation Chance", value: "precipitationChance"},
  {name: "Min Temperature", value: "tempMin"},
  {name: "Max Temperature", value: "tempMax"},
  {name: "Avg Temperature", value: "tempAvg"}
];

/** Weather depends options for ManageConditional when used from reviews (gate labels). */
export const REVIEW_WEATHER_DEPENDS_OPTIONS = WEATHER_DEPENDS_OPTIONS;

export const REVIEW_ALLOWED_TRIGGERS = ["date", "weekday", "weather"];

const CHECK_LABELS = {
  "===": "is",
  "!==": "is not",
  ">": "is greater than",
  "<": "is less than",
  ">=": "is greater than or equal to",
  "<=": "is less than or equal to"
};

const DEPENDS_LABELS = {
  weekday: "Current day is",
  date: "Date",
  ...WEATHER_DEPENDS_LABELS
};

/**
 * Transform ManageConditional submission values into a stored list item.
 */
export const transformManageConditionalSubmission = values => {
  const {trigger, depends: currentDepends, conditionArray: arr, operator: condOperator} = values;

  return {
    trigger,
    depends: currentDepends,
    check:
      currentDepends === "weekday" || currentDepends === "date"
        ? null
        : arr.map(cond => cond.check),
    compare:
      currentDepends === "weekday"
        ? arr[0].compare
        : arr.map(cond => {
            let {compare} = cond;
            if (cond?.frequency) {
              compare += `_${cond.frequency}`;
              if (cond?.lastDay) compare += "_last";
            }
            return compare;
          }),
    operator: condOperator
  };
};

/**
 * Reverse of transformManageConditionalSubmission — shape expected by ManageConditional `existing`.
 */
export const toManageConditionalExisting = item => {
  if (!item) return null;

  const {trigger, depends: currDepends, check, compare, operator: condOperator} = item;
  let conditionArray = [];

  let triggerDefault = trigger;
  if (!triggerDefault) {
    if (currDepends === "weekday" || currDepends === "date") triggerDefault = currDepends;
    else if (isWeatherConditionDepends(currDepends)) triggerDefault = "weather";
    else triggerDefault = "field";
  }

  if (currDepends !== "weekday" && Array.isArray(compare))
    compare.forEach((compareVal, compareIdx) => {
      if (check)
        conditionArray.push({check: check[compareIdx] ?? null, compare: compareVal ?? null});
      else {
        const parts = String(compareVal).split("_");
        conditionArray.push({
          check: null,
          compare: parts?.length >= 1 ? parts[0] : null,
          frequency: parts?.length >= 2 ? parts[1] : null,
          lastDay: parts?.length === 3
        });
      }
    });
  else if (compare) conditionArray = [{check: null, compare}];
  else conditionArray = [{check: null, compare: ""}];

  return {
    trigger: triggerDefault,
    condition: {depends: currDepends, operator: condOperator || "or", conditionArray}
  };
};

export const getReviewConditionItemLabel = item => {
  if (!item) return "";
  const dependsLabel = DEPENDS_LABELS[item.depends] || item.depends;
  let compareList = [];
  if (Array.isArray(item.compare)) compareList = item.compare;
  else if (item.compare) compareList = [item.compare];

  let checkList = [];
  if (Array.isArray(item.check)) checkList = item.check;
  else if (item.check) checkList = [item.check];

  const parts = compareList.map((compareVal, i) => {
    const normalizedCompare = isWeatherConditionDepends(item.depends)
      ? normalizeWeatherCompareValue(compareVal)
      : compareVal;
    let compareText = normalizedCompare;
    if (item.depends === "date" && typeof compareVal === "string") {
      const pieces = compareVal.split("_");
      if (pieces.length > 1) {
        compareText = `recurring ${pieces[1]}`;
        if (pieces[1] === "monthly" && pieces.length === 3) compareText += " on last day of month";
        else if (pieces[0]) compareText += ` starting ${dayjs(pieces[0]).format("MMM D")}`;
      } else compareText = dayjs(compareVal).format("MMM D");
    }
    if (item.depends === "precipitationChance") compareText = `${normalizedCompare}%`;
    if (item.depends === "rainfall" || item.depends === "cumulative")
      compareText = `${normalizedCompare} in`;
    if (["tempMin", "tempMax", "tempAvg"].includes(item.depends))
      compareText = `${normalizedCompare} °F`;

    const checkText =
      checkList[i] && CHECK_LABELS[checkList[i]] ? `${CHECK_LABELS[checkList[i]]} ` : "";
    return `${checkText}${compareText}`;
  });

  return `${dependsLabel} ${parts.join(` ${item.operator || "or"} `)}`.trim();
};

/**
 * Whether a review should appear in the required-review gate.
 * Missing/empty condition => always show. Weather with no data => not satisfied.
 */
export const shouldShowReview = (
  review,
  {dateForConditional = null, weatherContext = null} = {}
) => {
  const condition = review?.data?.condition || review?.condition;
  if (!condition?.list?.length) return true;

  const {list, operator = "or"} = condition;
  const results = [];

  for (let i = 0; i < list.length; i++) {
    const item = list[i];
    let check = false;

    if (isWeatherConditionDepends(item.depends)) {
      check = evaluateConditional(
        getWeatherConditionValue(weatherContext, item.depends),
        item.check,
        item.compare,
        {type: "number"},
        item.operator
      );
    } else if (item.depends === "weekday" || item.depends === "date") {
      check = evaluateConditional(
        dateForConditional,
        item.check,
        item.compare,
        item.depends,
        item.operator
      );
    }

    if (check && operator === "or") return true;
    results.push(check);
  }

  if (operator === "and") return results.every(Boolean);
  return results.some(Boolean);
};

export const filterReviewsForGate = (reviews, context) =>
  (reviews || []).filter(review => shouldShowReview(review, context));

export const isReviewWeatherField = value =>
  REVIEW_WEATHER_FIELDS.some(field => field.value === value);

export const formatWeatherFieldValue = (weatherEntry, fieldValue) => {
  if (!weatherEntry) return null;
  if (fieldValue === "precipitationChance") {
    const pct = weatherEntry.precipitationChance;
    if (pct === null || pct === undefined) return null;
    return `${Math.round(Number(pct))}%`;
  }
  if (fieldValue === "rainfall") {
    const val = weatherEntry.rainfall;
    if (val === null || val === undefined) return null;
    return `${val} in`;
  }
  if (fieldValue === "cumulative") {
    const val = weatherEntry.cumulative;
    if (val === null || val === undefined) return null;
    return `${val} in`;
  }
  if (["tempMin", "tempMax", "tempAvg"].includes(fieldValue)) {
    const val = weatherEntry[fieldValue];
    if (val === null || val === undefined) return null;
    return `${val} °F`;
  }
  return null;
};

/**
 * Build weatherContext in the same shape RenderChecksheet uses:
 * flat yesterday weather fields + today's precipitationChance.
 */
export const toWeatherContext = (weather, precipitationChance) => {
  if (!weather && !exists(precipitationChance)) return null;
  const weatherPayload = weather?.weather ? weather.weather : weather || {};
  return {
    ...weatherPayload,
    precipitationChance:
      precipitationChance ??
      weatherPayload?.precipitationChance ??
      weather?.precipitationChance ??
      weather?.precipitation_chance
  };
};

/**
 * Task for re-rendering a saved record: weather conditions read the record's day
 * (API `conditionWeather`) instead of the checksheet's "yesterday relative to now".
 * Falls back to the task as-is when the record has no `conditionWeather` field.
 */
export const withRecordConditionWeather = (task, record) => {
  if (!record || record.conditionWeather === undefined) return task;
  return {
    ...task,
    weather: record.conditionWeather,
    precipitationChance: record.conditionWeather?.precipitationChance ?? null
  };
};
